// TODO: vitest component test (pages are covered by Playwright e2e)
import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Badge, Button, Card, Input, Select, FormActions } from '@/components/ui'
import { Alert } from '@/components/ui/Alert'
import { Textarea } from '@/components/ui/Textarea'
import {
  usePermit,
  useCreatePermit,
  useUpdatePermit,
} from '@/hooks/useParkingPermits'
import { useVehicles, useVehicle } from '@/hooks/useVehicles'
import { useZones } from '@/hooks/useParkingZones'
import { useLeases } from '@/hooks/useLeases'
import { useProperty } from '@/hooks/useProperties'
import type { PermitType } from '@/types/parking'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useNotificationStore } from '@/stores/useNotificationStore'

interface PermitFormData {
  permit_type: PermitType | ''
  vehicle_id: string
  parking_zone_id: string
  lease_id: string
  slot_number: string
  sticker_number: string
  monthly_fee: string
  daily_fee: string
  started_at: string
  ended_at: string
  notes: string
}

export function ParkingPermitFormPage() {
  const { t } = useTranslation('parking')
  const { t: tc } = useTranslation('common')
  const { t: tl } = useTranslation('leases')
  // For new: /properties/:propertyId/parking/permits/new
  // For edit: /properties/:propertyId/parking/permits/:id/edit
  const { propertyId: routePropertyId, id } = useParams<{ propertyId: string; id: string }>()
  const navigate = useNavigate()
  const translateError = useApiErrorTranslator('parking')
  const addToast = useNotificationStore((s) => s.addToast)

  const isEditing = !!id
  const permitId = Number(id)

  const { data: existingPermit, isLoading: isLoadingPermit } = usePermit(
    isEditing ? permitId : 0
  )

  // Property ID from route when creating; 0 (disabled) for edit-only route
  const resolvedPropertyId = routePropertyId ? Number(routePropertyId) : 0

  const { data: propertyData } = useProperty(resolvedPropertyId)

  const { data: vehiclesData } = useVehicles(resolvedPropertyId)
  const { data: zonesData } = useZones(resolvedPropertyId)
  // Leases: active + pending_start for tenant permit picker
  const { data: leasesData } = useLeases(1, 'active,pending_start')

  const createPermit = useCreatePermit()
  const updatePermit = useUpdatePermit()

  const vehicles = vehiclesData?.data || []
  const zones = zonesData?.data || []
  const leases = leasesData?.data || []

  // Whether the operator has explicitly overridden the auto-filled lease
  const [leaseOverrideActive, setLeaseOverrideActive] = useState(false)

  // Client-side permit_type mismatch error (EPV-10)
  const [permitTypeMismatchError, setPermitTypeMismatchError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    watch,
    reset,
    control,
    formState: { errors },
    setValue,
  } = useForm<PermitFormData>({
    defaultValues: {
      permit_type: '',
      vehicle_id: '',
      parking_zone_id: '',
      lease_id: '',
      slot_number: '',
      sticker_number: '',
      monthly_fee: '',
      daily_fee: '',
      started_at: '',
      ended_at: '',
      notes: '',
    },
  })

  const permitType = watch('permit_type')
  const vehicleIdValue = watch('vehicle_id')

  // Fetch selected vehicle to get its parker_type + lease_summary
  const selectedVehicleId = vehicleIdValue ? Number(vehicleIdValue) : 0
  const { data: selectedVehicle } = useVehicle(selectedVehicleId)

  // The vehicle object from the list (lighter, no lease_summary)
  const selectedVehicleFromList = vehicles.find((v) => String(v.id) === vehicleIdValue)

  const isExternalVehicle = selectedVehicle?.parker_type === 'external'
  const isTenantVehicle = selectedVehicle?.parker_type === 'tenant'
  const vehicleLeaseSummary = selectedVehicle?.lease_summary

  // Conditional field visibility
  const showLeaseField =
    permitType === 'tenant_included' || permitType === 'tenant_addon'

  // Auto-fill lease from vehicle when a tenant vehicle is selected and lease picker should be visible
  useEffect(() => {
    if (isTenantVehicle && vehicleLeaseSummary && showLeaseField && !leaseOverrideActive) {
      setValue('lease_id', String(vehicleLeaseSummary.id))
    }
  }, [isTenantVehicle, vehicleLeaseSummary, showLeaseField, leaseOverrideActive, setValue])

  // Clear lease when vehicle changes (reset override flag too)
  useEffect(() => {
    setLeaseOverrideActive(false)
    if (!vehicleIdValue) {
      setValue('lease_id', '')
    }
  }, [vehicleIdValue, setValue])

  // Validate permit_type vs external vehicle (EPV-10)
  useEffect(() => {
    if (
      isExternalVehicle &&
      (permitType === 'tenant_included' || permitType === 'tenant_addon')
    ) {
      setPermitTypeMismatchError(t('errors.permit_type_mismatch_with_external_vehicle'))
    } else {
      setPermitTypeMismatchError(null)
    }
  }, [isExternalVehicle, permitType, t])

  const showMonthlyFee =
    permitType === 'tenant_addon' || permitType === 'monthly_external'
  const showDailyFee = permitType === 'daily_external'

  // Populate form when editing
  useEffect(() => {
    if (isEditing && existingPermit) {
      reset({
        permit_type: existingPermit.permit_type,
        vehicle_id: String(existingPermit.vehicle_id),
        parking_zone_id: existingPermit.parking_zone_id
          ? String(existingPermit.parking_zone_id)
          : '',
        lease_id: existingPermit.lease_id ? String(existingPermit.lease_id) : '',
        slot_number: existingPermit.slot_number || '',
        sticker_number: existingPermit.sticker_number || '',
        monthly_fee: existingPermit.monthly_fee != null ? String(existingPermit.monthly_fee) : '',
        daily_fee: existingPermit.daily_fee != null ? String(existingPermit.daily_fee) : '',
        started_at: existingPermit.started_at
          ? existingPermit.started_at.slice(0, 10)
          : '',
        ended_at: existingPermit.ended_at
          ? existingPermit.ended_at.slice(0, 10)
          : '',
        notes: existingPermit.notes || '',
      })
    }
  }, [isEditing, existingPermit, reset])

  const onSubmit = async (data: PermitFormData) => {
    if (!data.permit_type || !data.vehicle_id || !data.started_at) return

    // Block submission on mismatch
    if (permitTypeMismatchError) return

    try {
      if (isEditing) {
        await updatePermit.mutateAsync({
          id: permitId,
          parking_zone_id: data.parking_zone_id ? Number(data.parking_zone_id) : null,
          lease_id: data.lease_id ? Number(data.lease_id) : undefined,
          permit_type: data.permit_type as PermitType,
          slot_number: data.slot_number || null,
          sticker_number: data.sticker_number || null,
          monthly_fee: data.monthly_fee ? Number(data.monthly_fee) : null,
          daily_fee: data.daily_fee ? Number(data.daily_fee) : null,
          started_at: data.started_at,
          ended_at: data.ended_at || null,
          notes: data.notes || null,
        })
        if (routePropertyId) {
          navigate(`/properties/${routePropertyId}/parking/permits`)
        } else {
          navigate(-1)
        }
      } else {
        await createPermit.mutateAsync({
          vehicle_id: Number(data.vehicle_id),
          parking_zone_id: data.parking_zone_id ? Number(data.parking_zone_id) : undefined,
          lease_id: data.lease_id ? Number(data.lease_id) : undefined,
          permit_type: data.permit_type as PermitType,
          slot_number: data.slot_number || undefined,
          sticker_number: data.sticker_number || undefined,
          monthly_fee: data.monthly_fee ? Number(data.monthly_fee) : undefined,
          daily_fee: data.daily_fee ? Number(data.daily_fee) : undefined,
          started_at: data.started_at,
          ended_at: data.ended_at || undefined,
          notes: data.notes || undefined,
        })
        navigate(`/properties/${resolvedPropertyId}/parking/permits`)
      }
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  const permitTypeOptions = [
    { value: '', label: t('permits.permit_type') },
    { value: 'tenant_included', label: t('permits.types.tenant_included') },
    { value: 'tenant_addon', label: t('permits.types.tenant_addon') },
    { value: 'monthly_external', label: t('permits.types.monthly_external') },
    { value: 'daily_external', label: t('permits.types.daily_external') },
  ]

  const vehicleOptions = [
    { value: '', label: t('vehicles.license_plate') },
    ...vehicles.map((v) => ({
      value: String(v.id),
      label: `${v.license_plate}${v.brand ? ` — ${v.brand}` : ''}${v.model ? ` ${v.model}` : ''}`,
    })),
  ]

  const zoneOptions = [
    { value: '', label: t('zones.name') },
    ...zones.map((z) => ({
      value: String(z.id),
      label: `${z.name} (${z.available_slots} ${t('labels.available_slots')})`,
    })),
  ]

  const leaseOptions = [
    { value: '', label: tc('nav.leases') },
    ...leases.map((l) => ({
      value: String(l.id),
      label: `${l.unit?.name || `Unit #${l.unit_id}`} — ${l.customer?.name || `Customer #${l.customer_id}`}`,
    })),
  ]

  const propertyName = propertyData?.name

  const breadcrumbItems = isEditing
    ? [
        {
          label: t('permits.title'),
          href: routePropertyId
            ? `/properties/${routePropertyId}/parking/permits`
            : undefined,
        },
        { label: t('permits.edit') },
      ]
    : [
        {
          label: propertyName || String(resolvedPropertyId),
          href: `/properties/${resolvedPropertyId}`,
        },
        {
          label: t('permits.title'),
          href: `/properties/${resolvedPropertyId}/parking/permits`,
        },
        { label: t('permits.new_permit') },
      ]

  if (isEditing && isLoadingPermit) {
    return (
        <div className="space-y-6">
          <Card>
            <div className="h-64 animate-pulse bg-base-200 rounded" />
          </Card>
        </div>
    )
  }

  // Determine the auto-filled lease for the chip display
  const autoFilledLease = vehicleLeaseSummary
  const showLeaseChip = isTenantVehicle && showLeaseField && autoFilledLease && !leaseOverrideActive
  const showHiddenLeaseForExternal = isExternalVehicle && showLeaseField

  return (
      <div className="space-y-6">
        <Breadcrumbs items={breadcrumbItems} />

        <PageHeader
          title={isEditing ? t('permits.edit') : t('permits.new_permit')}
        />

        <Card>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Permit type mismatch error (EPV-10) */}
            {permitTypeMismatchError && (
              <Alert variant="error" icon={null} role="alert" data-testid="permit-type-mismatch-error">
                <span>{permitTypeMismatchError}</span>
              </Alert>
            )}

            <div className="grid gap-6 md:grid-cols-2">
              {/* Permit Type — always required */}
              <Controller
                name="permit_type"
                control={control}
                rules={{ required: tc('errors.required') }}
                render={({ field }) => (
                  <Select
                    label={t('permits.permit_type')}
                    options={permitTypeOptions}
                    error={errors.permit_type?.message}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                )}
              />

              {/* Vehicle — always required */}
              <Controller
                name="vehicle_id"
                control={control}
                rules={{ required: tc('errors.required') }}
                render={({ field }) => (
                  <Select
                    label={t('vehicles.license_plate')}
                    options={vehicleOptions}
                    error={errors.vehicle_id?.message}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                )}
              />

              {/* Lease — conditional on tenant permit types */}
              {showLeaseField && !showHiddenLeaseForExternal && (
                <>
                  {showLeaseChip ? (
                    /* Auto-filled: show read-only chip + Override link (EPV-10) */
                    <div className="form-control w-full">
                      <label className="label">
                        <span className="label-text">{tc('nav.leases')}</span>
                      </label>
                      <div
                        className="input input-bordered flex items-center justify-between bg-base-200"
                        data-testid="lease-auto-chip"
                      >
                        <span className="text-sm">
                          {t('permits.lease_auto_filled')}: <strong>{autoFilledLease.code}</strong>
                          {autoFilledLease.customer_name && ` · ${autoFilledLease.customer_name}`}
                          {' · '}
                          <Badge variant="ghost" size="sm">{tl(`status.${autoFilledLease.status}`)}</Badge>
                        </span>
                        <button
                          type="button"
                          className="text-xs text-primary underline ml-2"
                          onClick={() => setLeaseOverrideActive(true)}
                          data-testid="lease-override-btn"
                        >
                          {t('permits.override_lease')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Manual selection (external vehicle or override) */
                    <Controller
                      name="lease_id"
                      control={control}
                      rules={{ required: tc('errors.required') }}
                      render={({ field }) => (
                        <Select
                          label={tc('nav.leases')}
                          options={leaseOptions}
                          error={errors.lease_id?.message}
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                        />
                      )}
                    />
                  )}
                </>
              )}

              {/* External vehicle + lease field: show customer chip instead */}
              {showHiddenLeaseForExternal && selectedVehicleFromList && (
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text">{tc('nav.customers')}</span>
                  </label>
                  <div className="input input-bordered flex items-center bg-base-200" data-testid="external-customer-chip">
                    <Badge variant="ghost">
                      {selectedVehicleFromList.customer
                        ? `${selectedVehicleFromList.customer.first_name ?? ''} ${selectedVehicleFromList.customer.last_name ?? ''}`.trim()
                        : `Customer #${selectedVehicleFromList.customer_id}`}
                    </Badge>
                  </div>
                </div>
              )}

              {/* Monthly fee — conditional: tenant_addon or monthly_external */}
              {showMonthlyFee && (
                <Input
                  label={t('permits.monthly_fee')}
                  type="number"
                  min={0}
                  step="0.01"
                  error={errors.monthly_fee?.message}
                  {...register('monthly_fee', { required: tc('errors.required') })}
                />
              )}

              {/* Daily fee — conditional: daily_external */}
              {showDailyFee && (
                <Input
                  label={t('permits.daily_fee')}
                  type="number"
                  min={0}
                  step="0.01"
                  {...register('daily_fee')}
                />
              )}

              {/* Parking Zone */}
              <Controller
                name="parking_zone_id"
                control={control}
                render={({ field }) => (
                  <Select
                    label={t('zones.title')}
                    options={zoneOptions}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                )}
              />

              {/* Slot Number */}
              <Input
                label={t('permits.slot_number')}
                placeholder={t('permits.slot_number')}
                {...register('slot_number')}
              />

              {/* Sticker Number */}
              <Input
                label={t('permits.sticker_number')}
                placeholder={t('permits.sticker_number')}
                {...register('sticker_number')}
              />

              {/* Started At */}
              <Input
                label={t('permits.started_at')}
                type="date"
                error={errors.started_at?.message}
                {...register('started_at', { required: tc('errors.required') })}
              />

              {/* Ended At — optional */}
              <Input
                label={t('permits.ended_at')}
                type="date"
                {...register('ended_at')}
              />
            </div>

            {/* Notes */}
            <Textarea
              label={t('labels.notes')}
              rows={3}
              {...register('notes')}
            />

            <FormActions>
              <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
                {tc('actions.cancel')}
              </Button>
              <Button
                type="submit"
                loading={isEditing ? updatePermit.isPending : createPermit.isPending}
                disabled={!!permitTypeMismatchError}
              >
                {isEditing ? tc('actions.save_changes') : t('permits.add')}
              </Button>
            </FormActions>
          </form>
        </Card>
      </div>
  )
}
